/* Verified Evidence Vault — Hardened Sovereign Vault (Phase 1)
 *
 * Zero-Trust departmental page for high-value evidence uploads.
 *   1. 6-digit PIN gate (users set on first visit, master bypasses)
 *   2. 5-minute idle auto-lock — any keypress/click/scroll resets
 *   3. Every upload gets a client-side SHA-256 hash forwarded to the
 *      backend immutable audit ledger for forensic proof.
 *   4. High-security "lab" styling with a pulsing shield mascot.
 *
 * Phase 1 explicitly MOCKS the AES-256 client-side encryption piece;
 * the SHA-256 ledger is real and drives the immutable record.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { Shield, Lock, KeyRound, ScanLine, RefreshCw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import EvidenceVault from "@/components/EvidenceVault";
import { encryptFile, decryptFile, downloadSaltRecoveryFile, getSaltHex, getOrCreateSalt, setSaltHex } from "@/lib/vaultCrypto";

const AUTO_LOCK_SECONDS = 300; // 5 minutes

function VaultShieldMascot({ locked }) {
  return (
    <div className="relative w-32 h-32 mx-auto" aria-hidden>
      <div className={`absolute inset-0 rounded-full border ${locked ? "border-amber-500/30" : "border-emerald-500/30"} animate-[ping_2.4s_cubic-bezier(0,0,0.2,1)_infinite]`} />
      <div className={`absolute inset-2 rounded-full border-2 ${locked ? "border-amber-500/50" : "border-emerald-500/50"}`}
        style={{ animation: "spin 8s linear infinite" }} />
      <div className={`absolute inset-5 rounded-full border ${locked ? "border-amber-500/40" : "border-emerald-500/40"}`}
        style={{ animation: "spin 5s linear infinite reverse" }} />
      <div className="absolute inset-0 flex items-center justify-center">
        <Shield
          size={44}
          strokeWidth={1.4}
          className={locked ? "text-amber-500" : "text-emerald-400"}
          style={{ filter: `drop-shadow(0 0 18px ${locked ? "#D4AF37cc" : "#10B981cc"})` }}
        />
      </div>
    </div>
  );
}

export default function Vault() {
  const [status, setStatus] = useState(null);
  const [unlocked, setUnlocked] = useState(false);
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [previousPin, setPreviousPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [ledger, setLedger] = useState([]);
  const [idleSecs, setIdleSecs] = useState(AUTO_LOCK_SECONDS);
  const lastActivity = useRef(Date.now());

  // Passphrase — used to derive the AES-GCM key that encrypts every
  // upload client-side before it leaves the browser. Kept only in
  // component state; never persisted to storage.
  const [passphrase, setPassphrase] = useState("");
  const [passphraseSet, setPassphraseSet] = useState(false);
  const [showRecoveryPrompt, setShowRecoveryPrompt] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/vault/status");
      setStatus(data);
      if (data?.is_master) {
        // Master bypasses PIN entirely.
        setUnlocked(true);
      }
    } catch (err) {
      toast.error(formatApiError(err) || "Vault status unavailable.");
    }
  }, []);

  const loadLedger = useCallback(async () => {
    try {
      const { data } = await api.get("/vault/audit-log");
      setLedger(data?.entries || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { if (unlocked) loadLedger(); }, [unlocked, loadLedger]);

  // Fix-Now deep-link — when the vault unlocks, replay any pending
  // evidence-type request stashed by Royalty Hunter so the correct
  // form auto-opens without another click.
  useEffect(() => {
    if (!unlocked) return;
    const params = new URLSearchParams(window.location.search);
    const type = params.get("fixnow") || sessionStorage.getItem("vault:fix-now");
    if (!type) return;
    // Small delay so EvidenceVault has mounted its listener.
    const id = setTimeout(() => {
      window.dispatchEvent(new CustomEvent("bc-fix-now", { detail: { type } }));
      try { sessionStorage.removeItem("vault:fix-now"); } catch { /* ignore */ }
      // Clear the query param so refresh doesn't re-trigger the flow.
      const url = new URL(window.location.href);
      url.searchParams.delete("fixnow");
      window.history.replaceState({}, "", url.toString());
    }, 350);
    return () => clearTimeout(id);
  }, [unlocked]);

  // 5-minute idle auto-lock. Any interaction resets the timer.
  useEffect(() => {
    if (!unlocked || status?.is_master) return undefined;
    const bump = () => { lastActivity.current = Date.now(); };
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    const id = setInterval(() => {
      const elapsed = Math.floor((Date.now() - lastActivity.current) / 1000);
      const remain = Math.max(0, AUTO_LOCK_SECONDS - elapsed);
      setIdleSecs(remain);
      if (remain === 0) {
        setUnlocked(false);
        setPin("");
        toast.info("Vault auto-locked after 5 minutes of inactivity.");
      }
    }, 1000);
    return () => {
      events.forEach((e) => window.removeEventListener(e, bump));
      clearInterval(id);
    };
  }, [unlocked, status?.is_master]);

  const handleSetPin = async (e) => {
    e?.preventDefault();
    if (!/^\d{6}$/.test(pin)) { toast.error("PIN must be exactly 6 digits."); return; }
    if (pin !== confirmPin) { toast.error("Both PINs must match."); return; }
    setBusy(true);
    try {
      await api.post("/vault/set-pin", { pin, previous_pin: previousPin || undefined });
      toast.success("Vault PIN saved. Enter it now to unlock.");
      setConfirmPin("");
      setPreviousPin("");
      setPin("");
      await loadStatus();
    } catch (err) {
      toast.error(formatApiError(err) || "Could not save PIN.");
    } finally { setBusy(false); }
  };

  const handleVerifyPin = async (e) => {
    e?.preventDefault();
    if (!/^\d{6}$/.test(pin)) { toast.error("Enter your 6-digit PIN."); return; }
    setBusy(true);
    try {
      await api.post("/vault/verify-pin", { pin });
      setUnlocked(true);
      setPin("");
      lastActivity.current = Date.now();
      setIdleSecs(AUTO_LOCK_SECONDS);
      // Prompt for encryption passphrase — required before uploads work.
      const isFirstTime = !localStorage.getItem("vault:salt");
      setShowRecoveryPrompt(isFirstTime);
      toast.success("Vault unlocked. Set your encryption passphrase to enable uploads.");
    } catch (err) {
      toast.error(formatApiError(err) || "Incorrect PIN.");
    } finally { setBusy(false); }
  };

  const handleSetPassphrase = (e) => {
    e?.preventDefault();
    if ((passphrase || "").length < 8) {
      toast.error("Passphrase must be at least 8 characters.");
      return;
    }
    // Ensure a salt exists for PBKDF2 derivation.
    getOrCreateSalt();
    setPassphraseSet(true);
    if (showRecoveryPrompt) {
      downloadSaltRecoveryFile(status?.email || "vault-owner", getSaltHex());
      toast.success("Salt File downloaded. Store it offline — it is your only recovery artifact.");
      setShowRecoveryPrompt(false);
    } else {
      toast.success("Encryption enabled for this session.");
    }
  };

  const isMaster = !!status?.is_master;
  const needsSetup = !!status && !status.pin_set && !isMaster;

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 md:px-10 lg:px-14 py-12 lg:py-16">
        <div className="text-center mb-10">
          <div className="text-[10px] font-mono-tech uppercase tracking-[0.3em] text-emerald-400 mb-3">
            SOVEREIGN VAULT · CLASS-A
          </div>
          <h1 className="font-display text-4xl sm:text-5xl leading-tight mb-3">
            Verified Evidence <span className="text-emerald-400">Vault</span>
          </h1>
          <p className="text-sm text-neutral-400 max-w-xl mx-auto leading-relaxed">
            High-security environment for your legal receipts, master file
            proofs, and Chain of Title timeline. Every upload is hashed
            with SHA-256 and recorded on an append-only ledger for
            forensic verification.
          </p>
        </div>

        {!unlocked ? (
          /* ── Gate ── */
          <div className="max-w-md mx-auto bg-[#0a0a0a] border border-amber-500/40 p-8 lg:p-10 rounded" data-testid="vault-gate">
            <VaultShieldMascot locked />
            <div className="mt-6 text-center">
              <div className="text-[10px] font-mono-tech uppercase tracking-[0.22em] text-amber-500 mb-2">
                {needsSetup ? "Set your Vault PIN" : "Enter your Vault PIN"}
              </div>
              <p className="text-xs text-neutral-400 leading-relaxed mb-6">
                {needsSetup
                  ? "A 6-digit PIN protects the vault. You'll need it every time you unlock."
                  : "Your Vault auto-locks after 5 minutes of inactivity. Master accounts bypass this gate automatically."}
              </p>

              {needsSetup ? (
                <form onSubmit={handleSetPin} className="space-y-3" data-testid="vault-pin-setup">
                  <input
                    type="password" inputMode="numeric" maxLength={6} autoFocus
                    value={pin} onChange={(e) => setPin(e.target.value.replace(/[^\d]/g, ""))}
                    placeholder="6-digit PIN"
                    data-testid="vault-pin-input"
                    className="w-full text-center font-mono-tech text-2xl tracking-[0.5em] bg-black border border-white/10 focus:border-amber-500 outline-none px-4 py-4 text-white placeholder:text-neutral-600"
                  />
                  <input
                    type="password" inputMode="numeric" maxLength={6}
                    value={confirmPin} onChange={(e) => setConfirmPin(e.target.value.replace(/[^\d]/g, ""))}
                    placeholder="Confirm PIN"
                    data-testid="vault-pin-confirm"
                    className="w-full text-center font-mono-tech text-2xl tracking-[0.5em] bg-black border border-white/10 focus:border-amber-500 outline-none px-4 py-4 text-white placeholder:text-neutral-600"
                  />
                  <button
                    type="submit" disabled={busy}
                    data-testid="vault-pin-save"
                    className="w-full inline-flex items-center justify-center gap-2 bg-amber-500 text-black py-4 font-mono-tech text-xs uppercase tracking-[0.22em] font-semibold disabled:opacity-50"
                    style={{ boxShadow: "0 20px 60px -20px #D4AF37cc" }}
                  >
                    <KeyRound size={14} />
                    {busy ? "Saving…" : "Save Vault PIN"}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleVerifyPin} className="space-y-3" data-testid="vault-pin-verify">
                  <input
                    type="password" inputMode="numeric" maxLength={6} autoFocus
                    value={pin} onChange={(e) => setPin(e.target.value.replace(/[^\d]/g, ""))}
                    placeholder="••••••"
                    data-testid="vault-pin-verify-input"
                    className="w-full text-center font-mono-tech text-2xl tracking-[0.5em] bg-black border border-white/10 focus:border-emerald-500 outline-none px-4 py-4 text-white placeholder:text-neutral-600"
                  />
                  <button
                    type="submit" disabled={busy}
                    data-testid="vault-pin-verify-submit"
                    className="w-full inline-flex items-center justify-center gap-2 bg-emerald-500 text-black py-4 font-mono-tech text-xs uppercase tracking-[0.22em] font-semibold disabled:opacity-50"
                    style={{ boxShadow: "0 20px 60px -20px #10B981cc" }}
                  >
                    <ScanLine size={14} />
                    {busy ? "Verifying…" : "Unlock Vault"}
                  </button>
                </form>
              )}
              <div className="mt-5 text-[10px] font-mono-tech uppercase tracking-widest text-neutral-500">
                Class-A encryption · Ledgered on-chain internal
              </div>
            </div>
          </div>
        ) : (
          /* ── Unlocked Vault ── */
          <div data-testid="vault-body">
            {/* Live security status bar */}
            <div className="mb-6 flex flex-wrap items-center justify-between gap-3 border border-emerald-500/40 bg-emerald-500/5 px-5 py-3">
              <div className="flex items-center gap-3">
                <VaultShieldMascot locked={false} />
                <div>
                  <div className="text-[10px] font-mono-tech uppercase tracking-[0.22em] text-emerald-400">
                    Vault Session Active
                  </div>
                  <div className="text-xs text-neutral-300 mt-0.5">
                    Every upload is hashed with SHA-256 and appended to your ledger.
                  </div>
                </div>
              </div>
              {!isMaster && (
                <div className="font-mono-tech text-2xl font-semibold text-emerald-400 tabular-nums" data-testid="vault-idle-timer">
                  {String(Math.floor(idleSecs / 60)).padStart(2, "0")}:{String(idleSecs % 60).padStart(2, "0")}
                </div>
              )}
              {isMaster && (
                <div className="text-[10px] font-mono-tech uppercase tracking-widest text-amber-400">Master · No Lock</div>
              )}
            </div>

            {/* Passphrase step — required for AES-GCM encryption */}
            {!passphraseSet && (
              <div className="mb-6 border border-emerald-500/40 bg-emerald-500/5 p-6" data-testid="vault-passphrase-step">
                <div className="text-[10px] font-mono-tech uppercase tracking-[0.22em] text-emerald-400 mb-2">
                  AES-256 Encryption · Passphrase Required
                </div>
                <p className="text-xs text-neutral-300 leading-relaxed mb-4">
                  Set an encryption passphrase (≥ 8 characters). Every file
                  uploaded during this session is AES-GCM encrypted in your
                  browser before it leaves your device. On first use we
                  download a <strong>Salt File</strong> — the only recovery
                  artifact for this vault. Store it offline; losing both
                  the Salt File and your passphrase means the files
                  cannot be decrypted by anyone.
                </p>
                <form onSubmit={handleSetPassphrase} className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="password" autoFocus
                    value={passphrase} onChange={(e) => setPassphrase(e.target.value)}
                    placeholder="Encryption passphrase"
                    data-testid="vault-passphrase-input"
                    className="flex-1 bg-black border border-white/10 focus:border-emerald-500 outline-none px-4 py-3 text-sm text-white placeholder:text-neutral-600"
                  />
                  <button
                    type="submit"
                    data-testid="vault-passphrase-submit"
                    className="inline-flex items-center justify-center gap-2 bg-emerald-500 text-black px-5 py-3 font-mono-tech text-xs uppercase tracking-widest font-semibold hover:brightness-110"
                    style={{ boxShadow: "0 10px 30px -12px #10B981cc" }}
                  >
                    Activate Encryption
                  </button>
                </form>

                {/* Salt File Restore — for users switching devices */}
                <details className="mt-4 border-t border-emerald-500/20 pt-4">
                  <summary className="text-[10px] font-mono-tech uppercase tracking-widest text-emerald-400 cursor-pointer hover:text-emerald-300" data-testid="vault-restore-toggle">
                    Switching devices? Import a recovery salt
                  </summary>
                  <div className="mt-3 space-y-2">
                    <p className="text-[11px] text-neutral-400 leading-relaxed">
                      Paste the salt (hex) from your Salt File. Your
                      passphrase above will then derive the same key that
                      encrypted your existing files on the original device.
                    </p>
                    <input
                      type="text"
                      placeholder="Salt (hex, 32 chars)"
                      data-testid="vault-restore-salt"
                      className="w-full bg-black border border-white/10 focus:border-emerald-500 outline-none px-4 py-2 text-xs font-mono-tech text-white placeholder:text-neutral-600 uppercase tracking-wider"
                      onChange={(e) => {
                        const hex = e.target.value.trim().toLowerCase();
                        if (/^[0-9a-f]{32}$/.test(hex)) {
                          setSaltHex(hex);
                          toast.success("Salt imported. Enter your passphrase above to unlock encryption.");
                        }
                      }}
                    />
                  </div>
                </details>
              </div>
            )}

            {/* Departmental home: Evidence Vault form + Chain of Title */}
            <EvidenceVault
              showChainOfTitle
              encryptBefore={passphraseSet ? (file) => encryptFile(file, passphrase) : null}
              onUpload={async (file, ctx) => {
                try {
                  const buf = await file.arrayBuffer();
                  const hash = await crypto.subtle.digest("SHA-256", buf);
                  const hex = Array.from(new Uint8Array(hash))
                    .map((b) => b.toString(16).padStart(2, "0")).join("");
                  await api.post("/vault/audit-log", {
                    file_name: file.name,
                    file_size: file.size,
                    sha256: hex,
                    kind: ctx?.kind || "evidence",
                    context: {
                      ...(ctx || {}),
                      encrypted: !!ctx?.encrypted,
                      file_url: ctx?.file_url,
                    },
                  });
                  loadLedger();
                } catch { /* ledger append is best-effort */ }
              }}
            />

            {/* Immutable ledger */}
            <div className="mt-8 border border-white/5 bg-[#0a0a0a] p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-[10px] font-mono-tech uppercase tracking-[0.22em] text-emerald-400">
                    Immutable Audit Ledger
                  </div>
                  <div className="text-xs text-neutral-500 mt-1">
                    Append-only. Every hash is permanent proof this file existed at that moment.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={loadLedger}
                  data-testid="vault-ledger-refresh"
                  className="inline-flex items-center gap-2 text-xs font-mono-tech uppercase tracking-widest text-neutral-400 hover:text-white"
                >
                  <RefreshCw size={12} /> Refresh
                </button>
              </div>
              {ledger.length === 0 ? (
                <div className="text-sm text-neutral-500 py-6 text-center">
                  No entries yet — your ledger fills in as you upload evidence.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs font-mono-tech">
                    <thead className="text-neutral-500 uppercase tracking-widest">
                      <tr>
                        <th className="text-left py-2">Recorded</th>
                        <th className="text-left py-2">File</th>
                        <th className="text-left py-2">Size</th>
                        <th className="text-left py-2">SHA-256</th>
                        <th className="text-right py-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="text-neutral-300">
                      {ledger.map((row) => {
                        const ctxFileUrl = row.context?.file_url;
                        const isEncrypted = !!row.context?.encrypted;
                        return (
                          <tr key={row.ledger_id} className="border-t border-white/5">
                            <td className="py-2 pr-4 whitespace-nowrap text-neutral-400">
                              {new Date(row.recorded_at).toLocaleString()}
                            </td>
                            <td className="py-2 pr-4 truncate max-w-[240px]">
                              {row.file_name}
                              {isEncrypted && <span className="ml-2 text-[9px] px-1.5 py-0.5 border border-emerald-500/40 text-emerald-400 uppercase tracking-widest">AES-256</span>}
                            </td>
                            <td className="py-2 pr-4 text-neutral-400">{Math.round((row.file_size || 0) / 1024)} KB</td>
                            <td className="py-2 pr-4 truncate max-w-[240px] text-emerald-400" title={row.sha256}>
                              {row.sha256.slice(0, 20)}…
                            </td>
                            <td className="py-2 text-right">
                              {ctxFileUrl && (
                                <button
                                  type="button"
                                  data-testid={`vault-download-${row.ledger_id}`}
                                  onClick={async () => {
                                    try {
                                      const res = await fetch(ctxFileUrl);
                                      if (!res.ok) throw new Error("fetch failed");
                                      const blob = await res.blob();
                                      let out = blob;
                                      if (isEncrypted) {
                                        if (!passphrase) { toast.error("Enter your passphrase to decrypt."); return; }
                                        out = await decryptFile(blob, passphrase);
                                      }
                                      const url = URL.createObjectURL(out);
                                      const a = document.createElement("a");
                                      a.href = url; a.download = row.file_name;
                                      document.body.appendChild(a); a.click(); document.body.removeChild(a);
                                      URL.revokeObjectURL(url);
                                    } catch (err) {
                                      toast.error(err?.message === "OperationError" ? "Decryption failed — wrong passphrase?" : "Download failed.");
                                    }
                                  }}
                                  className="inline-flex items-center gap-1 text-emerald-400 hover:text-emerald-300 text-[10px] uppercase tracking-widest"
                                >
                                  Download{isEncrypted ? " + Decrypt" : ""}
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Manual lock */}
            <div className="mt-6 text-center">
              <button
                type="button"
                data-testid="vault-manual-lock"
                onClick={() => { setUnlocked(false); setPin(""); toast.info("Vault locked."); }}
                className="inline-flex items-center gap-2 border border-amber-500/40 text-amber-500 hover:bg-amber-500/10 px-4 py-2 font-mono-tech text-xs uppercase tracking-widest"
              >
                <Lock size={12} /> Lock Vault Now
              </button>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
